export interface TarotCardPositionReading {
    index: number;
    positionTitle: string;
    positionRole: string;
    cardName: string;
    symbol: string;
    text: string;
}

export interface TarotReadingResult {
    past: string;
    present: string;
    future: string;
    advice: string;
    synthesis: string;
    spreadId?: string;
    spreadName?: string;
    positions?: TarotCardPositionReading[];
}

export interface TarotSpreadConfig {
    id: 'single' | 'three' | 'cross' | 'horseshoe' | 'astrological';
    name: string;
    shortName: string;
    cardCount: number;
    badge: string;
    layoutType: 'single' | 'row3' | 'cross' | 'horseshoe' | 'astrological';
    positionNames: { title: string; role: string }[];
}

export const TAROT_SPREADS: TarotSpreadConfig[] = [
    {
        id: 'single',
        name: 'Tirage 1 Carte (Flash)',
        shortName: '1 Carte (Flash)',
        cardCount: 1,
        badge: '1 Carte • Réponse Rapide',
        layoutType: 'single',
        positionNames: [
            { title: 'Tendance Flash & Réponse', role: 'Réponse directe et énergie dominante' }
        ]
    },
    {
        id: 'three',
        name: 'Tirage 3 Cartes (Passé / Présent / Futur)',
        shortName: '3 Cartes (Passé / Présent / Futur)',
        cardCount: 3,
        badge: '3 Cartes • Évolution Temporelle',
        layoutType: 'row3',
        positionNames: [
            { title: '1. Origines & Passé', role: 'Racines et atouts antérieurs' },
            { title: '2. Situation Présente', role: 'Énergies et défis actuels' },
            { title: '3. Futur & Aboutissement', role: 'Conseil et évolution prévisible' }
        ]
    },
    {
        id: 'cross',
        name: 'Tirage en Croix (5 Cartes)',
        shortName: '5 Cartes (En Croix)',
        cardCount: 5,
        badge: '5 Cartes • Grand Classique',
        layoutType: 'cross',
        positionNames: [
            { title: '1. À Gauche : Le Pour (Forces)', role: 'Ce qui vous aide, vos atouts et forces motrices' },
            { title: '2. À Droite : Le Contre (Obstacles)', role: 'Ce qui freine, pièges et défis à contourner' },
            { title: '3. En Haut : L\'Évolution (Tendance)', role: 'La direction que prend votre situation' },
            { title: '4. En Bas : La Réponse (Résultat)', role: 'L\'aboutissement direct de votre question' },
            { title: '5. Au Centre : La Synthèse', role: 'Le message clé et conseil ultime des arcanes' }
        ]
    },
    {
        id: 'horseshoe',
        name: 'Tirage en Fer à Cheval (7 Cartes)',
        shortName: '7 Cartes (Fer à Cheval)',
        cardCount: 7,
        badge: '7 Cartes • Analyse Approfondie',
        layoutType: 'horseshoe',
        positionNames: [
            { title: '1. Le Passé Récent', role: 'Ce qui a déclenché votre situation' },
            { title: '2. Le Présent Immédiat', role: 'Vos ressentis et état actuel' },
            { title: '3. Espoirs & Craintes', role: 'Ce que votre inconscient espère ou redoute' },
            { title: '4. Obstacles & Défis', role: 'Les épreuves à surmonter sur votre route' },
            { title: '5. Influences Extérieures', role: 'L\'entourage, proches et facteurs extérieurs' },
            { title: '6. Meilleur Chemin & Conseil', role: 'L\'attitude gagnante à adopter' },
            { title: '7. Issue Probable & Dénouement', role: 'L\'aboutissement final de la situation' }
        ]
    },
    {
        id: 'astrological',
        name: 'Tirage Astrologique (13 Cartes)',
        shortName: '13 Cartes (Roue Astrale)',
        cardCount: 13,
        badge: '13 Cartes • Les 12 Maisons',
        layoutType: 'astrological',
        positionNames: [
            { title: 'Maison I - Vous & Énergie', role: 'État d\'esprit, vitalité et affirmation de soi' },
            { title: 'Maison II - Finances & Matériel', role: 'Ressources financières, gains et sécurité matérielle' },
            { title: 'Maison III - Communication', role: 'Échanges, démarches, proches et déplacements' },
            { title: 'Maison IV - Foyer & Famille', role: 'Maison, racines, vie intime et équilibre familial' },
            { title: 'Maison V - Amour & Passions', role: 'Sentiments, rencontres de cœur et créativité' },
            { title: 'Maison VI - Travail & Santé', role: 'Activités professionnelles courantes et bien-être' },
            { title: 'Maison VII - Couple & Alliances', role: 'Le partenaire de vie, contrats et associations' },
            { title: 'Maison VIII - Transformation', role: 'Évolutions profondes, héritage et renaissance' },
            { title: 'Maison IX - Horizons & Projets', role: 'Horizons lointains, élévation morale et projets' },
            { title: 'Maison X - Destin & Carrière', role: 'Vocation, réputation et accomplissement social' },
            { title: 'Maison XI - Amitiés & Avenir', role: 'Soutiens amicaux, espoirs et projets partagés' },
            { title: 'Maison XII - Karma & Secrets', role: 'Inconscient, obstacles secrets et délivrance' },
            { title: 'Maison XIII - Synthèse Globale', role: 'La guidance céleste majeure qui couronne votre ciel' }
        ]
    }
];

// Significations ésotériques approfondies pour chaque Arcane Majeur
export const ARCANA_DETAILS: Record<string, {
    symbol: string;
    pastText: string;
    presentText: string;
    futureText: string;
    adviceText: string;
    pourText: string;
    contreText: string;
}> = {
    "Le Mat": {
        symbol: "l'élan vers l'inconnu, l'innocence créatrice et l'audace sans filtre",
        pastText: "Vous avez traversé une phase de saut dans l'inconnu, où vous avez préféré faire confiance à votre intuition plutôt qu'à la raison sécurisante. Cet élan spontané a déblayé le terrain de préjugés obsolètes.",
        presentText: "Vous vous trouvez à l'aube d'un cycle totalement neuf. L'énergie du Bateleur/Mat insuffle un vent de liberté et d'aventure : n'ayez pas peur de vous affranchir des règles trop rigides pour créer votre propre voie.",
        futureText: "Une opportunité inattendue et hors des sentiers battus va se présenter. En osant l'inattendu, vous débloquerez une situation que vous pensiez figée.",
        adviceText: "Embrassez la liberté sans peur du lendemain. Votre spontanéité est votre meilleure alliée pour ouvrir de nouvelles portes.",
        pourText: "Votre audace et votre capacité à vous réinventer librement vous donnent un avantage décisif.",
        contreText: "Attention aux coups de tête irréfléchis et au manque de préparation qui pourraient vous déstabiliser."
    },
    "Le Bateleur": {
        symbol: "le potentiel créateur, la maîtrise des outils et le début des possibles",
        pastText: "Votre passé récent a été marqué par l'acquisition de nouveaux savoirs et le rassemblement de vos talents personnels. Vous avez su réunir les éléments nécessaires pour construire vos projets.",
        presentText: "Tous les outils du succès sont désormais étalés devant vous sur la table du magicien. Votre esprit est vif, créatif et prêt à transformer une simple idée en une réalité tangible.",
        futureText: "Un projet ambitieux prendra corps très rapidement. Grâce à votre initiative et à votre habileté naturelle, vous poserez les bases d'un succès durable.",
        adviceText: "Passez à l'action sans attendre la perfection théorique. Vous possédez déjà tout ce qu'il faut pour réussir.",
        pourText: "Votre vivacité d'esprit, votre éloquence et votre sens de l'initiative ouvrent toutes les portes.",
        contreText: "Méfiez-vous de la dispersion et de la tentation de commencer trop de choses sans les achever."
    },
    "La Papesse": {
        symbol: "la sagesse intuitive, le secret bien gardé et la patience sacrée",
        pastText: "Une période de gestation silencieuse et d'observation méticuleuse vous a permis de comprendre des vérités cachées. Vous avez accumulé une grande maturité spirituelle.",
        presentText: "L'heure est au retrait stratégique et à l'écoute de votre voix intérieure. Les événements se préparent en coulisse : ne précipitez rien et faites confiance au temps.",
        futureText: "Un secret sera révélé ou une compréhension fulgurante viendra éclairer vos doutes. Ce qui était voilé deviendra d'une clarté limpide.",
        adviceText: "Fiez-vous à vos ressentis subtils et gardez vos projets secrets jusqu'au moment idéal de leur éclosion.",
        pourText: "Votre intuition infaillible et votre calme vous protègent contre toute erreur de jugement.",
        contreText: "Gare au mutisme excessif ou à une passivité prolongée qui retarderait des décisions nécessaires."
    },
    "L'Impératrice": {
        symbol: "l'abondance créative, le rayonnement de l'esprit et la concrétisation heureuse",
        pastText: "Vous avez semé des graines de créativité et d'amour qui ont généré une belle dynamique autour de vous. Votre bienveillance a porté ses premiers fruits.",
        presentText: "Une énergie d'épanouissement et de concrétisation vous entoure. Vos idées sont inspirées et vous avez le pouvoir de séduire et de convaincre avec une grande élégance.",
        futureText: "Une période de récolte généreuse et de rayonnement personnel s'annonce. Vos initiatives porteront des résultats au-delà de vos espérances.",
        adviceText: "Exprimez vos talents avec générosité et foi en votre potentiel. L'abondance répond à votre confiance.",
        pourText: "Votre charme naturel, votre créativité fertile et votre sens du contact facilitent toutes vos entreprises.",
        contreText: "Évitez la futilité ou le bavardage stérile qui dilueraient votre puissance d'action."
    },
    "L'Empereur": {
        symbol: "la structure solide, la volonté d'acier et l'ancrage matériel",
        pastText: "Vous avez dû faire preuve d'une discipline rigoureuse et poser des bases méthodiques pour sécuriser votre environnement et protéger vos proches.",
        presentText: "Vous êtes aux commandes de votre existence. Votre sang-froid et votre sens de l'organisation vous permettent d'imposer le respect et de structurer vos démarches.",
        futureText: "Une consolidation financière ou matérielle majeure se profile. Vous obtiendrez l'accord ou la position d'autorité que vous visiez.",
        adviceText: "Incarnez la fermeté bienveillante. Structurez vos projets avec méthode pour leur donner une portée inébranlable.",
        pourText: "Votre sens de l'organisation, votre rigueur et votre autorité naturelle vous assurent une stabilité sans faille.",
        contreText: "Prenez garde à la rigidité excessive ou à l'entêtement face aux idées novatrices."
    },
    "Le Pape": {
        symbol: "la guidance spirituelle, l'accord moral et la transmission de sagesse",
        pastText: "Vous avez bénéficié de conseils avisés ou du soutien d'une figure d'autorité bienveillante qui vous a transmis des valeurs fondamentales.",
        presentText: "Les énergies favorisent les alliances morales, la médiation et la recherche d'harmonie. Un climat de bienveillance réciproque protège vos démarches actuelles.",
        futureText: "Une réconciliation, un contrat moral ou un accord officiel viendra sceller vos efforts dans un esprit de loyauté mutuelle.",
        adviceText: "Privilégiez la diplomatie, la vérité et le respect des engagements souscrits pour garantir la paix.",
        pourText: "Votre sens moral élevé, votre écoute et votre diplomatie attirent la confiance d'autrui.",
        contreText: "Évitez les dogmes rigides ou le besoin excessif d'obtenir l'approbation de tout le monde."
    },
    "L'Amoureux": {
        symbol: "le choix du cœur, l'alignement passionnel et la croisée des chemins",
        pastText: "Un choix sentimental ou éthique important a exigé de vous un alignement sincère entre vos désirs profonds et votre réalité.",
        presentText: "Vous êtes à un carrefour décisif. Deux options s'offrent à vous : l'une guidée par la raison rassurante, l'autre par l'élan vibrant du cœur.",
        futureText: "L'union amoureuse ou le partenariat désiré se concrétisera dès lors que vous aurez pris une décision claire et authentique.",
        adviceText: "Écoutez l'appel sincère de votre cœur sans laisser la peur du choix vous paralyser.",
        pourText: "Votre authenticité et votre capacité à aimer sincèrement créent des liens magnétiques profonds.",
        contreText: "Attention aux doutes perpétuels et à l'indécision chronique qui risqueraient de laisser passer le bon moment."
    },
    "Le Chariot": {
        symbol: "la victoire triomphale, la maîtrise des contradictions et l'avancement rapide",
        pastText: "Vous avez traversé des tensions ou des oppositions en gardant fermement le cap grâce à votre volonté d'acier.",
        presentText: "Les obstacles cèdent sur votre passage. Vous êtes porté par un élan dynamique irrésistible qui propulse vos affaires vers l'avant.",
        futureText: "Succès éclatant et reconnaissance publique au bout du chemin. Un déplacement ou un voyage s'avérera particulièrement bénéfique.",
        adviceText: "Maintenez l'équilibre entre la passion et la raison, et foncez vers vos objectifs sans hésiter.",
        pourText: "Votre dynamisme conquérant, votre courage et votre vitesse d'exécution vous mènent tout droit à la victoire.",
        contreText: "Attention à la précipitation ou à l'autoritarisme qui pourraient heurter vos compagnons de route."
    },
    "La Justice": {
        symbol: "l'équilibre parfait, la clarté de la vérité et le règlement équitable",
        pastText: "Une exigence d'intégrité ou une décision officielle vous a contraint à faire le tri et à assumer la responsabilité de vos actes.",
        presentText: "L'heure est au bilan objectif et au rétablissement de la vérité. Ce qui est juste prévaudra et les malentendus se dissipent.",
        futureText: "Dénouement juridique, administratif ou relationnel favorable. L'équité sera rétablie et vous obtiendrez gain de cause.",
        adviceText: "Agissez avec une honnêteté irréprochable. La vérité est votre plus puissant bouclier.",
        pourText: "Votre lucidité impartiale, votre droiture et votre respect des règles vous garantissent d'être dans votre droit.",
        contreText: "Gare à la sévérité excessive ou au jugement trop tranchant qui manquerait de compassion humaine."
    },
    "L'Ermite": {
        symbol: "la lumière intérieure, la recherche de vérité et la maturité du sage",
        pastText: "Une traversée du désert ou un temps de solitude volontaire vous a permis de mûrir une sagesse personnelle précieuse.",
        presentText: "Prenez le temps d'éclairer votre chemin avec la lanterne de la réflexion. Ne vous laissez pas bousculer par le bruit extérieur.",
        futureText: "L'aboutissement d'une recherche longue et patiente apportera une réponse définitive et une grande paix d'esprit.",
        adviceText: "Avancez pas à pas avec prudence et discernement. La patience construit les réussites éternelles.",
        pourText: "Votre profondeur d'analyse, votre recul et votre expérience vous évitent tous les pièges de surface.",
        contreText: "Méfiez-vous de l'isolement excessif ou de la lenteur qui pourraient décourager vos partenaires."
    },
    "La Roue de Fortune": {
        symbol: "le tournant du destin, l'opportunité saisie et la roue qui tourne en bien",
        pastText: "Un changement imprévu de situation a balayé des habitudes anciennes, vous obligeant à vous adapter avec réactivité.",
        presentText: "La roue tourne à nouveau en votre faveur. Le destin remet les cartes en jeu et vous offre un vent d'opportunités rares.",
        futureText: "Une accélération soudaine et un coup de chance propulseront vos affaires vers des sommets inespérés.",
        adviceText: "Saisissez l'opportunité au vol sans hésiter : la chance sourit à ceux qui savent surfer sur la vague.",
        pourText: "La providence céleste et le facteur chance s'alignent parfaitement pour favoriser votre ascension.",
        contreText: "Ne vous reposez pas uniquement sur le hasard : consolidez vos acquis dès que la roue tourne en haut."
    },
    "La Force": {
        symbol: "la maîtrise de soi, le courage doux et la victoire de l'esprit sur la matière",
        pastText: "Vous avez dû dompter vos peurs et faire preuve d'une immense patience pour traverser une période exigeante sans fléchir.",
        presentText: "Vous possédez une puissance intérieure remarquable. Vous triomphez des épreuves non par la violence, mais par la fermeté et la douceur.",
        futureText: "Victoire assurée sur les tensions ou les adversités. Vous prendrez l'ascendant moral avec calme et dignité.",
        adviceText: "Utilisez la douceur persuasive plutôt que le rapport de force. Votre calme intérieur est invincible.",
        pourText: "Votre magnétisme serein, votre résilience et votre force morale inspirent le respect absolu.",
        contreText: "Attention à ne pas retenir trop d'émotions sous pression : canalisez votre force sans vous épuiser."
    },
    "Le Pendu": {
        symbol: "le lâcher-prise salutaire, la nouvelle perspective et le sacrifice fécond",
        pastText: "Une phase de blocage ou de suspension temporaire vous a forcé à regarder votre situation sous un angle totalement inédit.",
        presentText: "Il est inutile de forcer les événements actuellement. Acceptez cette pause comme une incubation nécessaire pour mûrir votre projet.",
        futureText: "Un déclic libérateur surviendra d'un coup, inversant la situation en votre faveur et dissolvant les blocages.",
        adviceText: "Lâchez prise sur le résultat immédiat et observez la situation sous un autre jour.",
        pourText: "Votre sens du don de soi et votre vision détachée des apparences vous permettent de voir ce que d'autres ignorent.",
        contreText: "Attention au sentiment d'impuissance ou à la posture de victime qui paralyserait l'action."
    },
    "La Mort": {
        symbol: "la métamorphose profonde, la fin d'un cycle et la renaissance lumineuse",
        pastText: "La fin inéluctable d'une situation ou d'une illusion a fait de la place nette dans votre vie, balayant le superflu.",
        presentText: "Vous vivez une grande mue intérieure. En abandonnant ce qui a fait son temps, vous vous préparez à accueillir une énergie régénérée.",
        futureText: "Une renaissance spectaculaire s'annonce. Après l'épuratif, un nouveau chapitre infiniment plus beau commence.",
        adviceText: "Ne retenez pas le passé qui s'éteint; célébrez l'émergence de la nouvelle version de vous-même.",
        pourText: "Votre courage à tourner la page et votre capacité de régénération vous rendent inarrêtable.",
        contreText: "L'attachement nostalgique à des structures mortes ne fait que prolonger inutilement la transition."
    },
    "L'Arcane sans nom": {
        symbol: "la métamorphose profonde, la fin d'un cycle et la renaissance lumineuse",
        pastText: "La fin inéluctable d'une situation ou d'une illusion a fait de la place nette dans votre vie, balayant le superflu.",
        presentText: "Vous vivez une grande mue intérieure. En abandonnant ce qui a fait son temps, vous vous préparez à accueillir une énergie régénérée.",
        futureText: "Une renaissance spectaculaire s'annonce. Après l'épuratif, un nouveau chapitre infiniment plus beau commence.",
        adviceText: "Ne retenez pas le passé qui s'éteint; célébrez l'émergence de la nouvelle version de vous-même.",
        pourText: "Votre lucidité pour vous détacher de ce qui ne sert plus votre destinée supérieure.",
        contreText: "La peur du vide ou de l'inconnu avant que les nouvelles graines ne germent."
    },
    "Tempérance": {
        symbol: "la guérison douce, la fluidité relationnelle et la régénération énergétique",
        pastText: "Vous avez su apaiser des conflits et rétablir l'harmonie grâce à votre mesure et votre sens de la conciliation.",
        presentText: "Les énergies de paix et d'équilibre coulent abondamment. Vos échanges sont empreints de douceur, de patience et de compréhension.",
        futureText: "Rétablissement parfait de la santé, des finances ou des relations. Une période de calme réparateur s'installe.",
        adviceText: "Privilégiez la modération et la communication pacifique dans toutes vos démarches.",
        pourText: "Votre sérénité contagieuse et votre talent de médiateur désamorcent toutes les querelles.",
        contreText: "Gare à la tiédeur ou aux compromis excessifs qui dilueraient vos convictions profondes."
    },
    "Le Diable": {
        symbol: "la passion ardente, l'attraction magnétique et le défi du pouvoir personnel",
        pastText: "Une forte attraction, une tentation ou un attachement passionnel a testé votre discernement et votre libre arbitre.",
        presentText: "Désir intense et créativité débordante sont au rendez-vous. Attention toutefois à ne pas vous laisser enchaîner par la possessivité.",
        futureText: "Réussite matérielle ou passion amoureuse incandescente. Vous saurez tirer parti des opportunités concrètes avec habileté.",
        adviceText: "Canalisez cette immense énergie passionnelle sans devenir esclave de vos pulsions ou de vos peurs.",
        pourText: "Votre magnétisme envoûtant, votre sensualité et votre flair redoutable pour les affaires matérielles.",
        contreText: "Le risque de dépendance affective, de manipulation ou d'orgueil démesuré."
    },
    "La Maison Dieu": {
        symbol: "la libération foudroyante, la prise de conscience et la reconstruction solide",
        pastText: "Un choc salutaire ou une révélation inattendue a fait s'écrouler une structure illusoire que vous entreteniez à tort.",
        presentText: "Les fausses certitudes s'effondrent pour laisser place à la vérité brute. C'est une bénédiction déguisée qui vous libère d'un poids.",
        futureText: "Sur les ruines de l'illusion, vous rebâtirez des fondations indestructibles et authentiques.",
        adviceText: "Accueillez la vérité avec courage : ce qui s'écroule ne méritait pas de durer.",
        pourText: "La rupture avec un mensonge ou une prison dorée vous restitue instantanément votre liberté originelle.",
        contreText: "Le sentiment temporaire de vertige face à un bouleversement inattendu des repères."
    },
    "L'Étoile": {
        symbol: "l'espoir radieux, la protection céleste et la pureté des intentions",
        pastText: "Vous avez conservé la foi et l'espérance dans les moments sombres, guidé par une bonne étoile discrète mais présente.",
        presentText: "Inspiration, sérénité et grâce entourent votre situation. Vous êtes en harmonie avec l'Univers et vos vœux reçoivent une bénédiction.",
        futureText: "Réalisation heureuse d'un souhait cher à votre âme. Vos projets s'épanouiront sous une protection astrale absolue.",
        adviceText: "Gardez une confiance totale en votre avenir et exprimez votre vérité avec authenticité.",
        pourText: "La bienveillance des astres, votre sincérité désarmante et la clarté de vos intentions.",
        contreText: "Prenez garde à l'angélisme ou à la naïveté face à des personnes moins bien intentionnées."
    },
    "La Lune": {
        symbol: "l'intuition profonde, l'imagination féconde et la traversée des ombres",
        pastText: "Des doutes, des illusions ou des peurs inconscientes ont pu vous donner l'impression de marcher dans le brouillard.",
        presentText: "Vos facultés d'intuition et votre sensibilité créative sont décuplées. Attention cependant aux fausses interprétations.",
        futureText: "La lumière du jour dissipera les malentendus, révélant la véritable nature des sentiments ou des affaires en jeu.",
        adviceText: "Fiez-vous à vos rêves et à votre instinct sans vous laisser effrayer par des ombres imaginaires.",
        pourText: "Votre réceptivité télépathique et la puissance de votre imagination poétique et artistique.",
        contreText: "Les angoisses nocturnes infondées, les illusions trompeuses ou les non-dits pesants."
    },
    "Le Soleil": {
        symbol: "la clarté absolue, le bonheur partagé, le succès et l'amour rayonnant",
        pastText: "Des moments de joie vraie et de transparence ont éclairé votre trajectoire, vous redonnant foi en la bonté humaine.",
        presentText: "Pleine lumière sur votre existence ! Chaleur, amitié, succès et réussite amoureuse illuminent votre quotidien.",
        futureText: "Couronnement victorieux de vos démarches. Bonheur éclatant, union chaleureuse et prospérité partagée.",
        adviceText: "Rayonnez sans retenue et partagez votre bonheur avec ceux que vous aimez.",
        pourText: "L'éclat irrésistible de votre joie de vivre, la générosité de votre cœur et la réussite totale.",
        contreText: "Gare à l'éblouissement ou à l'excès de confiance qui ferait négliger les petits détails."
    },
    "Le Jugement": {
        symbol: "l'éveil spirituel, l'appel du destin et la délivrance définitive",
        pastText: "Une prise de conscience foudroyante vous a libéré d'une culpabilité ou d'un fardeau ancien qui vous pesait.",
        presentText: "Un appel intérieur irrésistible vous invite à entrer dans une nouvelle dimension de votre vie. L'heure de la renaissance a sonné.",
        futureText: "Une nouvelle inespérée, un pardon ou une victoire morale viendra couronner vos efforts avec éclat.",
        adviceText: "Répondez à l'appel de votre vocation profonde sans vous retourner sur hier.",
        pourText: "Une libération soudaine, une réponse attendue depuis longtemps qui arrive enfin pour vous délivrer.",
        contreText: "Hésiter à répondre à l'appel du changement par peur de quitter une zone de confort."
    },
    "Le Monde": {
        symbol: "l'accomplissement total, la perfection du cycle et la réussite absolue",
        pastText: "Vous avez mené à bien un parcours d'intégration et d'apprentissage complet qui a fait de vous une personne accomplie.",
        presentText: "Vous touchez à la plénitude. Toutes les pièces du puzzle s'assemblent harmonieusement dans votre vie.",
        futureText: "Triomphe total, reconnaissance publique ou réalisation suprême de vos aspirations les plus élevées.",
        adviceText: "Célébrez votre victoire avec gratitude et ouvrez-vous à la beauté du monde.",
        pourText: "L'harmonie universelle, l'aboutissement d'un cycle majeur et la consécration de vos talents.",
        contreText: "Craindre la fin d'une belle étape : tout accomplissement est le tremplin d'une aventure encore plus vaste."
    }
};

/**
 * Générateur universel d'interprétations pour tous les types de tirages (1, 3, 5, 7, 13 cartes) - Sans Emojis
 */
export function generateMultiSpreadTarotReading(spreadId: string, question: string, cards: string[]): TarotReadingResult {
    const config = TAROT_SPREADS.find(s => s.id === spreadId) || TAROT_SPREADS[1];
    const userQStr = question && question.trim().length > 3 ? `pour votre question : « ${question.trim()} »` : "pour votre guidance générale";

    const positions: TarotCardPositionReading[] = [];

    config.positionNames.forEach((pos, idx) => {
        const cardName = cards[idx] || "Le Soleil";
        const detail = ARCANA_DETAILS[cardName] || ARCANA_DETAILS["Le Soleil"];

        let positionText = "";

        if (config.id === 'single') {
            positionText = `**${cardName}** (${detail.symbol}) se dresse comme l'oracle principal ${userQStr}.\n\n` +
                           `Cette lame majeure concentre les énergies dominantes du moment : ${detail.presentText}\n\n` +
                           `**Guidance Flash :** ${detail.adviceText}`;
        } else if (config.id === 'three') {
            if (idx === 0) {
                positionText = `**Passé & Origines (${cardName}) :** ${detail.pastText} Cette influence (${detail.symbol}) éclaire les racines de votre situation.`;
            } else if (idx === 1) {
                positionText = `**Présent & Énergie Actuelle (${cardName}) :** ${detail.presentText} La force de ${cardName} (${detail.symbol}) est le pivot central du moment.`;
            } else {
                positionText = `**Futur & Aboutissement (${cardName}) :** ${detail.futureText} Cette lame annonce une issue portée par ${detail.symbol}.`;
            }
        } else if (config.id === 'cross') {
            if (idx === 0) {
                positionText = `**Le Pour / Vos Forces (${cardName}) :** ${detail.pourText} ${detail.symbol} agit comme un puissant moteur en votre faveur.`;
            } else if (idx === 1) {
                positionText = `**Le Contre / Les Obstacles (${cardName}) :** ${detail.contreText} Prenez garde aux freins liés à cette énergie.`;
            } else if (idx === 2) {
                positionText = `**L'Évolution / La Tendance (${cardName}) :** ${detail.futureText} La dynamique prend une trajectoire claire vers ${detail.symbol}.`;
            } else if (idx === 3) {
                positionText = `**La Réponse / Le Résultat (${cardName}) :** ${detail.presentText} L'aboutissement direct montre que ${cardName} tranchera la question.`;
            } else {
                positionText = `**La Synthèse (${cardName}) :** ${detail.adviceText} Au centre de la croix, ${cardName} délivre la conclusion majeure du tirage.`;
            }
        } else if (config.id === 'horseshoe') {
            const stepsTexts = [
                `**Passé Récent :** ${detail.pastText}`,
                `**Présent Immédiat :** ${detail.presentText}`,
                `**Espoirs & Craintes :** ${detail.symbol} reflète vos attentes intérieures. ${detail.pourText}`,
                `**Obstacles à franchir :** ${detail.contreText}`,
                `**Influences Extérieures :** L'entourage et le contexte sont marqués par l'énergie de ${cardName} (${detail.symbol}).`,
                `**Conseil de l'Oracle :** ${detail.adviceText}`,
                `**Issue Probable :** ${detail.futureText}`
            ];
            positionText = `**${cardName}** — ${stepsTexts[idx] || detail.presentText}`;
        } else if (config.id === 'astrological') {
            const houseTitles = [
                `**Maison I (Vous & Vitalité) :** ${detail.presentText}`,
                `**Maison II (Finances & Gains) :** ${detail.pourText} Sur le plan matériel, ${cardName} apporte son énergie d'abondance.`,
                `**Maison III (Communication & Échanges) :** Les relations et démarches sont rythmées par ${detail.symbol}.`,
                `**Maison IV (Foyer & Famille) :** ${detail.pastText} Le cadre intime et familial trouve sa stabilité.`,
                `**Maison V (Amour, Cœur & Créativité) :** Côté sentiments, ${cardName} fait rayonner l'épanouissement.`,
                `**Maison VI (Travail & Quotidien) :** ${detail.adviceText} Vos activités exigent cette méthode.`,
                `**Maison VII (Couple & Partenariats) :** L'alliance avec l'autre est éclairée par ${detail.symbol}.`,
                `**Maison VIII (Transformations & Secrets) :** ${detail.futureText}`,
                `**Maison IX (Voyages & Projets) :** De nouveaux horizons s'ouvrent, portés par ${cardName}.`,
                `**Maison X (Carrière & Destin) :** Reconnaissance et accomplissement au zénith de votre parcours.`,
                `**Maison XI (Amitiés & Espoirs) :** Vos projets d'avenir reçoivent l'appui de vos alliés.`,
                `**Maison XII (Karma & Inconscient) :** Libération des épreuves et sérénité retrouvée.`,
                `**Maison XIII (Synthèse Astrale) :** ${detail.adviceText} La roue astrale conclut sur une issue lumineuse.`
            ];
            positionText = `**${cardName}** — ${houseTitles[idx] || detail.presentText}`;
        }

        positions.push({
            index: idx,
            positionTitle: pos.title,
            positionRole: pos.role,
            cardName: cardName,
            symbol: detail.symbol,
            text: positionText
        });
    });

    const leadCard = cards[cards.length - 1] || cards[0] || "Le Monde";
    const leadDetail = ARCANA_DETAILS[leadCard] || ARCANA_DETAILS["Le Monde"];

    const globalAdvice = `**Conseil de l'Oracle :**\n${leadDetail.adviceText} Prenez appui sur les forces révélées par ${cards.slice(0, 3).join(', ')} pour concrétiser vos aspirations ${userQStr}.`;
    const globalSynthesis = `**Synthèse du Tirage (${config.name}) :**\nLes arcanes révèlent un alignement particulièrement clair. Les énergies en jeu confirment une traversée constructive et une ouverture majeure vers la réussite et la sérénité.`;

    // Backward compatibility fields
    const pastVal = positions[0]?.text || leadDetail.pastText;
    const presentVal = positions[1]?.text || positions[0]?.text || leadDetail.presentText;
    const futureVal = positions[2]?.text || positions[positions.length - 1]?.text || leadDetail.futureText;

    return {
        past: pastVal,
        present: presentVal,
        future: futureVal,
        advice: globalAdvice,
        synthesis: globalSynthesis,
        spreadId: config.id,
        spreadName: config.name,
        positions: positions
    };
}

export function getRandomOrMatchingTarotReading(question: string, cards: string[]): TarotReadingResult {
    const spreadId = cards.length === 1 ? 'single' : cards.length === 5 ? 'cross' : cards.length === 7 ? 'horseshoe' : cards.length >= 12 ? 'astrological' : 'three';
    return generateMultiSpreadTarotReading(spreadId, question, cards);
}
